import { Drawer, Empty, Segmented, Select, Space, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';

import type { DayResult, JobFlight } from '../../../src/types.ts';
import { filterFlights, type MaxStops } from '../lib/flights.ts';
import { splitLegs } from '../lib/itinerary.ts';
import { FlightCard } from './FlightCard.tsx';

interface Props {
  day: DayResult | null;
  maxStops: MaxStops;
  route: string;
  onClose: () => void;
  onBook: (flight: JobFlight, day: DayResult) => void;
}

type SortKey = 'price' | 'duration' | 'departure';

function totalMinutes(f: JobFlight): number {
  return splitLegs(f).reduce((sum, l) => sum + (l.totalMinutes ?? 0), 0) || Number.MAX_SAFE_INTEGER;
}

/** Every fare a day returned, from every provider, with filters and a booking hand-off. */
export function DayDrawer({ day, maxStops, route, onClose, onBook }: Props) {
  const [sort, setSort] = useState<SortKey>('price');
  const [airline, setAirline] = useState<string | null>(null);
  const [returnDate, setReturnDate] = useState<string | null>(null);

  const all = useMemo(() => filterFlights(day?.flights, maxStops), [day, maxStops]);

  const airlines = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of all) m.set(f.airlineCode || f.airline, f.airline || f.airlineCode || '?');
    return [...m].map(([value, label]) => ({ value, label }));
  }, [all]);

  const returnDates = useMemo(() => [...new Set(all.map((f) => f.returnDate).filter((d): d is string => Boolean(d)))].sort(), [all]);

  const flights = useMemo(() => {
    let list = all;
    if (airline) list = list.filter((f) => (f.airlineCode || f.airline) === airline);
    if (returnDate) list = list.filter((f) => f.returnDate === returnDate);
    const sorted = [...list];
    if (sort === 'price') sorted.sort((a, b) => a.price - b.price);
    else if (sort === 'duration') sorted.sort((a, b) => totalMinutes(a) - totalMinutes(b));
    else sorted.sort((a, b) => String(a.departTime ?? '').localeCompare(String(b.departTime ?? '')));
    return sorted;
  }, [all, airline, returnDate, sort]);

  const cheapestPrice = all.length ? Math.min(...all.map((f) => f.price)) : null;

  return (
    <Drawer
      open={day !== null}
      onClose={onClose}
      size={640}
      className="day-drawer"
      title={
        <div>
          <div>{day ? dayjs(day.date).format('dddd, D MMMM YYYY') : ''}</div>
          <Typography.Text type="secondary" style={{ fontWeight: 400, fontSize: 13 }}>
            {route}
            {returnDates.length ? ` · ${returnDates.length === 1 ? `return ${returnDates[0]}` : `${returnDates.length} return dates`}` : ''}
            {' · '}
            {all.length} fare{all.length === 1 ? '' : 's'}
            {day?.errors.length ? ` · ${day.errors.length} provider${day.errors.length === 1 ? '' : 's'} failed` : ''}
          </Typography.Text>
        </div>
      }
    >
      <Space wrap style={{ marginBottom: 12 }}>
        <Segmented
          size="small"
          value={sort}
          onChange={(v) => setSort(v as SortKey)}
          options={[
            { label: 'Cheapest', value: 'price' },
            { label: 'Fastest', value: 'duration' },
            { label: 'Departure', value: 'departure' },
          ]}
        />
        {airlines.length > 1 && (
          <Select
            size="small"
            allowClear
            placeholder="Any airline"
            style={{ minWidth: 160 }}
            value={airline ?? undefined}
            onChange={(v) => setAirline(v ?? null)}
            options={airlines}
          />
        )}
        {returnDates.length > 1 && (
          <Select
            size="small"
            allowClear
            placeholder="Any return date"
            style={{ minWidth: 150 }}
            value={returnDate ?? undefined}
            onChange={(v) => setReturnDate(v ?? null)}
            options={returnDates.map((d) => ({ value: d, label: `Return ${d}` }))}
          />
        )}
      </Space>

      {day?.errors.length ? (
        <div style={{ marginBottom: 12 }}>
          {day.errors.map((e) => (
            <Tag key={e.provider} color="orange" bordered={false} title={e.error}>
              {e.provider}: {e.error.length > 60 ? `${e.error.slice(0, 60)}…` : e.error}
            </Tag>
          ))}
        </div>
      ) : null}

      {flights.length === 0 ? (
        <Empty description="No fares match the current filters." />
      ) : (
        <div className="fare-list">
          {flights.map((f, i) => (
            <FlightCard
              key={`${f.provider}-${f.returnDate ?? ''}-${f.segments.map((s) => s.flightNo).join('/')}-${i}`}
              flight={f}
              rank={f.price === cheapestPrice ? 0 : i + 1}
              onBook={(fl) => day && onBook(fl, day)}
            />
          ))}
        </div>
      )}
    </Drawer>
  );
}
